import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  HelpCircle, DollarSign, ClipboardCheck, Clock, ShieldCheck, ArrowRight, Mail,
} from 'lucide-react';
import LandingHeader from '@/components/landing/LandingHeader';
import LandingFooter from '@/components/landing/LandingFooter';
import FaqAccordion from '@/components/faq/FaqAccordion';
import FaqCategoryNav from '@/components/faq/FaqCategoryNav';
import { FAQ_CATEGORIES, buildFaqJsonLd, findQuestionLocation } from '@/data/faqContent';
import { PAGE_META } from '@/data/pageMeta';
import usePageMeta from '@/hooks/usePageMeta';
import useStructuredData from '@/hooks/useStructuredData';
import shared from '@/styles/public.module.css';
import styles from '@/components/faq/faq.module.css';

const CONTACT_PATH = '/contact';

// Icons live here rather than in the content module, which stays pure data.
const CATEGORY_ICONS = {
  basics: HelpCircle,
  cost: DollarSign,
  eligibility: ClipboardCheck,
  timeline: Clock,
  'attorney-privacy': ShieldCheck,
};

// Fallback so an unmapped category id (e.g. added to faqContent.js without a
// matching icon entry) degrades gracefully instead of crashing the page.
const FALLBACK_ICON = HelpCircle;

const FIRST_QUESTION_ID = FAQ_CATEGORIES[0].questions[0].id;

export default function FAQ() {
  const navigate = useNavigate();
  const location = useLocation();
  const [openItems, setOpenItems] = useState([FIRST_QUESTION_ID]);
  const [activeCategory, setActiveCategory] = useState(FAQ_CATEGORIES[0].id);

  usePageMeta(PAGE_META.faq);
  // FAQPage structured data, from the same array this page renders.
  useStructuredData(buildFaqJsonLd());

  // If the URL has a hash, open and scroll to the question or category it
  // targets. Otherwise scroll to top. A hash targeting a specific question
  // replaces the open set so that question is the only one open.
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (!hash) {
      window.scrollTo(0, 0);
      return;
    }

    const match = findQuestionLocation(hash);
    if (match) {
      setOpenItems([match.questionId]);
      setActiveCategory(match.categoryId);
    } else if (FAQ_CATEGORIES.some((category) => category.id === hash)) {
      setActiveCategory(hash);
    }

    const timer = setTimeout(() => {
      const target = document.getElementById(hash);
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 120);

    return () => clearTimeout(timer);
  }, [location.hash]);

  // Highlight whichever category is currently nearest the top of the viewport.
  // The top margin tracks the sticky nav (70px) plus the anchor offset.
  useEffect(() => {
    const sections = FAQ_CATEGORIES.map((category) =>
      document.getElementById(category.id),
    ).filter(Boolean);

    if (sections.length === 0) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) setActiveCategory(visible[0].target.id);
      },
      { rootMargin: '-86px 0px -60% 0px', threshold: 0 },
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  const handleCategorySelect = (categoryId) => {
    setActiveCategory(categoryId);
    const target = document.getElementById(categoryId);
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div data-testid="faq-page">
      <LandingHeader />

      <main>

      {/* Hero */}
      <section className={shared.section} data-testid="faq-hero">
        <div className={shared.wrap}>
          <div className={styles.hero}>
            <span className={styles.badge}>Common Questions</span>
            <h1 className={styles.title}>Answers Before You Apply</h1>
            <p className={styles.lead}>
              What an advance costs, what happens if your case does not succeed, and what your
              attorney has to do — explained plainly, before you fill anything out.
            </p>
            <div className={styles.heroActions}>
              <button
                type="button"
                className={`${shared.btn} ${shared.btnAction}`}
                onClick={() => navigate('/apply')}
                data-testid="faq-hero-apply-btn"
              >
                Apply for Funding <ArrowRight size={18} aria-hidden="true" />
              </button>
              <a
                className={`${shared.btn} ${shared.btnQuiet}`}
                href={CONTACT_PATH}
                onClick={(e) => { e.preventDefault(); navigate(CONTACT_PATH); }}
                data-testid="faq-hero-contact-btn"
              >
                <Mail size={17} aria-hidden="true" /> Contact Us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Questions */}
      <section className={`${shared.section} ${shared.sectionPanel}`}>
        <div className={`${shared.wrap} ${styles.layout}`}>
          <FaqCategoryNav
            categories={FAQ_CATEGORIES}
            activeId={activeCategory}
            onSelect={handleCategorySelect}
          />
          <div>
            {FAQ_CATEGORIES.map((category) => (
              <FaqAccordion
                key={category.id}
                category={category}
                icon={CATEGORY_ICONS[category.id] || FALLBACK_ICON}
                openItems={openItems}
                onOpenChange={setOpenItems}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section
        className={`${shared.section} ${shared.sectionDark}`}
        data-testid="faq-final-cta"
      >
        <div className={shared.wrap}>
          <div className={styles.finalInner}>
            <h2>Still Have Questions?</h2>
            <p>
              Email us and we will get back to you. If you are ready to see what funders would
              offer on your case, applying takes a few minutes and costs nothing.
            </p>
            <div className={styles.finalActions}>
              <button
                type="button"
                className={`${shared.btn} ${shared.btnAccent}`}
                onClick={() => navigate('/apply')}
                data-testid="faq-cta-apply-btn"
              >
                Apply for Funding <ArrowRight size={18} aria-hidden="true" />
              </button>
              <a
                className={`${shared.btn} ${shared.btnOnDark}`}
                href={CONTACT_PATH}
                onClick={(e) => { e.preventDefault(); navigate(CONTACT_PATH); }}
                data-testid="faq-cta-contact-btn"
              >
                <Mail size={17} aria-hidden="true" /> Contact Us
              </a>
            </div>
          </div>
        </div>
      </section>
      </main>


      <LandingFooter />
    </div>
  );
}
